import { useMemo, useState } from 'react';
import { useTaxonomyAudit } from './useSectorTaxonomy';
import { Pager } from '../../components/Pager';
import { formatMappingStatus, shiftIsoDate, todayIsoDate } from './sectorTaxonomy.types';

const TRANSITIONS = [
  '',
  'pending_review->confirmed',
  'pending_review->rejected',
  'confirmed->rejected',
  'rejected->confirmed',
];

// 탭 E — 감사 이력 타임라인. sector_mapping.updated_at + reviewer + review_note 기반.
export function AuditLogTab(): JSX.Element {
  const [reviewer, setReviewer] = useState('');
  const [mappingIdInput, setMappingIdInput] = useState('');
  const [transition, setTransition] = useState<string>('');
  const [from, setFrom] = useState(shiftIsoDate(30));
  const [to, setTo] = useState(todayIsoDate());
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const mappingId = useMemo(() => {
    const n = Number(mappingIdInput);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [mappingIdInput]);

  const query = useTaxonomyAudit({
    page,
    pageSize,
    reviewer: reviewer.trim() || undefined,
    mappingId,
    from: from || undefined,
    to: to || undefined,
    statusTransition: transition || undefined,
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <div className="pca-filter">
        <div className="pca-filter__group">
          <label className="pca-filter__label">reviewer</label>
          <input
            className="pca-input"
            placeholder="leo"
            value={reviewer}
            onChange={(e) => {
              setReviewer(e.target.value);
              setPage(0);
            }}
          />
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label">mapping_id</label>
          <input
            className="pca-input"
            placeholder=""
            value={mappingIdInput}
            onChange={(e) => {
              setMappingIdInput(e.target.value);
              setPage(0);
            }}
            style={{ width: 120 }}
          />
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label">전이</label>
          <select
            className="pca-select"
            value={transition}
            onChange={(e) => {
              setTransition(e.target.value);
              setPage(0);
            }}
          >
            {TRANSITIONS.map((t) => (
              <option key={t || 'any'} value={t}>
                {t || '모든 전이'}
              </option>
            ))}
          </select>
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label">from</label>
          <input
            type="date"
            className="pca-input"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(0);
            }}
          />
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label">to</label>
          <input
            type="date"
            className="pca-input"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(0);
            }}
          />
        </div>
      </div>

      {query.isError && (
        <div className="pca-warning-banner" style={{ marginTop: 8 }}>
          감사 이력 로드 실패: {(query.error as Error).message}
        </div>
      )}

      <div className="tx-audit-list" style={{ marginTop: 12 }}>
        {query.isLoading && (
          <div className="tx-audit-row">
            <span className="pca-muted">로딩 중…</span>
          </div>
        )}
        {!query.isLoading && items.length === 0 && (
          <div className="tx-audit-row">
            <span className="pca-muted">조건에 맞는 감사 이력이 없습니다.</span>
          </div>
        )}
        {items.map((entry) => {
          // MVP: 백엔드는 최종 status 만 제공. from→to 전이는 차후 지원 예정.
          const transitionLabel = entry.status
            ? `→ ${formatMappingStatus(entry.status)}`
            : '변경';
          const occurred = entry.updatedAt ?? entry.reviewedAt ?? '';
          const key = `${entry.mappingId}-${occurred}`;
          return (
            <div className="tx-audit-row" key={key}>
              <span className="tx-audit-row__time">{occurred ? occurred.slice(0, 19) : '—'}</span>
              <span className="tx-audit-row__transition">
                <span className="tx-audit-row__reviewer">{entry.reviewer ?? '—'}</span>
                {' · '}
                {transitionLabel}
                {' · '}
                mapping_id={entry.mappingId}
                {entry.canonicalDisplayName && (
                  <>
                    {' · '}
                    {entry.sourceSectorName ?? '—'} →{' '}
                    <strong>{entry.canonicalDisplayName}</strong>
                  </>
                )}
                {entry.reviewNote && (
                  <div className="pca-muted" style={{ marginTop: 4 }}>
                    note: {entry.reviewNote}
                  </div>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <Pager page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
    </div>
  );
}
